/* Copyright (c) 2024 MOCA Systems, Inc.  All rights reserved. */
import { EventBoundary, DisplayObject, EventMode, Point, EventSystem } from 'pixi.js-legacy';

type HitTestFunction = (displayObject: DisplayObject, location: Point) => boolean;
type PruneTestFunction = (displayObject: DisplayObject, location: Point) => boolean;

/**
 * A IFancyEventSystem adds some additional data to the EventSystem so we know when to ignore/short circuit the recursive hit tests
 */
interface IFancyEventSystem extends EventSystem {
	startEventAlreadyDetected: boolean;
}

/**
 * A FancyEventBoundary overrides the recursive hitTest method to ignore interaction tests if there was already a match this frame.
 * This start event is currently being managed in the `HammerWrapper.ts` file
 */
export class FancyEventBoundary extends EventBoundary {
	/**
	 * Stores a reference to the EventSystem. We then cast it as a `IFancyEventSystem` for typey-ness
	 * @private
	 */
	private _eventSystem: IFancyEventSystem;
	constructor(root: DisplayObject, eventSystem: EventSystem) {
		super(root);
		this._eventSystem = eventSystem as IFancyEventSystem;
	}
	/**
	 * Checks whether the display object passes hit testing for the given location.
	 * @param x {number} The X position of the source point (used to check overlap against this EventBoundary)
	 * @param y {number} The Y position of the source point (used to check overlap against this EventBoundary)
	 */
	public override hitTest(x: number, y: number): DisplayObject | null {
		if (this._eventSystem.startEventAlreadyDetected) {
			return null;
		}

		return super.hitTest(x, y);
	}
	/**
	 * Recursive implementation for hitTest. This is invoked by the EventSystem.
	 * Touchplan has a very wide scene graph with many interactive elements, so a large amount of hitTest's need to be invoked each tick, testing against many potential hit candidates
	 * This re-implements the dirty hack we've used in every previous version of PIXI which ignores every subsequent check once the `start` event was dispatched, meaning there was at least one match this frame.
	 * This start event is currently being managed in the `HammerWrapper.ts` file
	 * @param currentTarget {DisplayObject} The DisplayObject that is to be hit tested.
	 * @param eventMode {EventMode} The event mode for the currentTarget or one of its parents.
	 * @param location {Point} The location that is being tested for overlap.
	 * @param testFn {HitTestFunction} Callback that determines whether the target passes hit testing. This callback can assume that pruneFn failed to prune the display object.
	 * @param pruneFn {PruneTestFunction} Callback that determiness whether the target and all of its children cannot pass the hit test. It is used as a preliminary optimization to prune entire subtrees of the scene graph.
	 * @protected
	 */
	protected override hitTestRecursive(
		currentTarget: DisplayObject,
		eventMode: EventMode,
		location: Point,
		testFn: HitTestFunction,
		pruneFn: PruneTestFunction
	): DisplayObject[] | null {
		if (this._eventSystem.startEventAlreadyDetected) {
			return null;
		}

		return super.hitTestRecursive(currentTarget, eventMode, location, testFn, pruneFn);
	}
}
